/**
 * Create Plan Modal
 * Form to create a new subscription plan
 */

import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import styled from 'styled-components';
import { Modal } from '../../components/ui/Modal';
import { Input } from '../../components/ui/Input';
import { Button } from '../../components/ui/Button';
import planService, { CreatePlanRequest } from '../../services/api/planService';

interface CreatePlanModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const DEFAULT_FORM: CreatePlanRequest = {
  name: '',
  description: '',
};

export const CreatePlanModal = ({ isOpen, onClose }: CreatePlanModalProps) => {
  const queryClient = useQueryClient();
  const [form, setForm] = useState<CreatePlanRequest>(DEFAULT_FORM);

  const createMutation = useMutation({
    mutationFn: () => planService.createPlan(form),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['plans'] });
      handleClose();
    },
  });

  const handleClose = () => {
    setForm(DEFAULT_FORM);
    createMutation.reset();
    onClose();
  };

  const handleChange = (field: keyof CreatePlanRequest, value: string | number) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const isSubmitDisabled = !form.name.trim() || createMutation.isPending;

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title="Create Plan"
      size="md"
      footer={
        <>
          <Button variant="secondary" onClick={handleClose}>
            Cancel
          </Button>
          <Button
            variant="primary-blue"
            onClick={() => createMutation.mutate()}
            disabled={isSubmitDisabled}
            isLoading={createMutation.isPending}
          >
            Create Plan
          </Button>
        </>
      }
    >
      <FormContainer>
        {createMutation.isError && (
          <ErrorMessage>
            {(createMutation.error as any)?.response?.data?.message ||
              'Failed to create plan. Please try again.'}
          </ErrorMessage>
        )}

        <Field>
          <Label htmlFor="plan-name">
            Name <Required>*</Required>
          </Label>
          <Input
            id="plan-name"
            value={form.name}
            onChange={(e) => handleChange('name', e.target.value)}
            placeholder="e.g. Starter, Professional, Enterprise"
            fullWidth
          />
        </Field>

        <Field>
          <Label htmlFor="plan-description">Description</Label>
          <TextArea
            id="plan-description"
            value={form.description ?? ''}
            onChange={(e) => handleChange('description', e.target.value)}
            placeholder="Brief description of this plan..."
            rows={3}
          />
        </Field>
      </FormContainer>
    </Modal>
  );
};

export default CreatePlanModal;

// ==================== Styled Components ====================

const FormContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing[5]};
`;

const Field = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing[1]};
`;

const Label = styled.label`
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  font-weight: ${({ theme }) => theme.typography.fontWeight.medium};
  color: ${({ theme }) => theme.colors.text.primary};
`;

const Required = styled.span`
  color: ${({ theme }) => theme.colors.status.error.text};
  margin-left: 2px;
`;

const TextArea = styled.textarea`
  padding: ${({ theme }) => theme.spacing[3]};
  border: 1px solid ${({ theme }) => theme.colors.borders.default};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  color: ${({ theme }) => theme.colors.text.primary};
  font-family: inherit;
  resize: vertical;
  transition: ${({ theme }) => theme.transitions.fast};
  width: 100%;
  box-sizing: border-box;

  &:focus {
    outline: none;
    border-color: ${({ theme }) => theme.colors.primary.base};
    box-shadow: ${({ theme }) => theme.shadows.ring};
  }

  &::placeholder {
    color: ${({ theme }) => theme.colors.text.light};
  }
`;

const ErrorMessage = styled.div`
  padding: ${({ theme }) => theme.spacing[3]};
  background: ${({ theme }) => theme.colors.status.error.bg};
  border: 1px solid ${({ theme }) => theme.colors.status.error.border};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  color: ${({ theme }) => theme.colors.status.error.text};
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
`;
